interface BufferEntry {
  level: 'error' | 'warn' | 'unhandled';
  message: string;
  timestamp: string;
  source?: string;
}

interface DiagnosticSnapshot {
  capturedAt: string;
  browser: {
    userAgent: string;
    platform: string;
    language: string;
    languages: readonly string[];
    cookiesEnabled: boolean;
    online: boolean;
    doNotTrack: string | null;
  };
  screen: {
    width: number;
    height: number;
    availWidth: number;
    availHeight: number;
    colorDepth: number;
    pixelRatio: number;
  };
  viewport: {
    innerWidth: number;
    innerHeight: number;
  };
  network: {
    type: string;
    effectiveType: string;
    downlink: number;
    rtt: number;
  } | null;
  memory: {
    jsHeapSizeLimit: number;
    totalJSHeapSize: number;
    usedJSHeapSize: number;
  } | null;
  storage: {
    localStorageUsedBytes: number;
    localStorageKeyCount: number;
  };
  consoleErrors: BufferEntry[];
  truncated?: boolean;
}

const MAX_BUFFER_SIZE = 50;

// The snapshot rides in the support-request POST, which AWS WAF 403s (with no CORS headers) over 8 KB.
const MAX_SNAPSHOT_ERRORS = 5;
const MAX_ERROR_MESSAGE_CHARS = 300;
const MAX_SNAPSHOT_JSON_BYTES = 1536;

const errorBuffer: BufferEntry[] = [];

const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;

function pushEntry(entry: BufferEntry): void {
  if (errorBuffer.length >= MAX_BUFFER_SIZE) {
    errorBuffer.shift();
  }
  errorBuffer.push(entry);
}

function formatArgs(args: unknown[]): string {
  return args
    .map(arg => {
      if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
      if (typeof arg === 'object') {
        try { return JSON.stringify(arg); } catch { return String(arg); }
      }
      return String(arg);
    })
    .join(' ');
}

console.error = function (...args: unknown[]) {
  pushEntry({
    level: 'error',
    message: formatArgs(args),
    timestamp: new Date().toISOString()
  });
  originalConsoleError.apply(console, args);
};

console.warn = function (...args: unknown[]) {
  pushEntry({
    level: 'warn',
    message: formatArgs(args),
    timestamp: new Date().toISOString()
  });
  originalConsoleWarn.apply(console, args);
};

window.addEventListener('error', (event: ErrorEvent) => {
  pushEntry({
    level: 'unhandled',
    message: event.message || 'Unknown error',
    timestamp: new Date().toISOString(),
    source: event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : undefined
  });
});

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  const message = event.reason instanceof Error
    ? `${event.reason.name}: ${event.reason.message}`
    : String(event.reason);

  pushEntry({
    level: 'unhandled',
    message: `Unhandled Promise Rejection: ${message}`,
    timestamp: new Date().toISOString()
  });
});

function recentErrors(): BufferEntry[] {
  return errorBuffer.slice(-MAX_SNAPSHOT_ERRORS).map(entry =>
    entry.message.length > MAX_ERROR_MESSAGE_CHARS
      ? { ...entry, message: `${entry.message.slice(0, MAX_ERROR_MESSAGE_CHARS)}…` }
      : entry
  );
}

function getLocalStorageStats(): { localStorageUsedBytes: number; localStorageKeyCount: number } {
  try {
    let totalBytes = 0;
    const keyCount = localStorage.length;
    for (let i = 0; i < keyCount; i++) {
      const key = localStorage.key(i);
      if (key) {
        totalBytes += key.length + (localStorage.getItem(key)?.length ?? 0);
      }
    }
    return { localStorageUsedBytes: totalBytes * 2, localStorageKeyCount: keyCount };
  } catch {
    return { localStorageUsedBytes: 0, localStorageKeyCount: 0 };
  }
}

export const diagnosticsUtils = {
  getDiagnosticSnapshot(): DiagnosticSnapshot {
    // Chrome/Edge only
    const nav = navigator as any;
    const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
    const networkInfo = connection
      ? {
          type: connection.type ?? 'unknown',
          effectiveType: connection.effectiveType ?? 'unknown',
          downlink: connection.downlink ?? 0,
          rtt: connection.rtt ?? 0
        }
      : null;

    // Chrome only
    const perf = performance as any;
    const memoryInfo = perf.memory
      ? {
          jsHeapSizeLimit: perf.memory.jsHeapSizeLimit,
          totalJSHeapSize: perf.memory.totalJSHeapSize,
          usedJSHeapSize: perf.memory.usedJSHeapSize
        }
      : null;

    return {
      capturedAt: new Date().toISOString(),
      browser: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        languages: navigator.languages,
        cookiesEnabled: navigator.cookieEnabled,
        online: navigator.onLine,
        doNotTrack: navigator.doNotTrack
      },
      screen: {
        width: screen.width,
        height: screen.height,
        availWidth: screen.availWidth,
        availHeight: screen.availHeight,
        colorDepth: screen.colorDepth,
        pixelRatio: window.devicePixelRatio
      },
      viewport: {
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight
      },
      network: networkInfo,
      memory: memoryInfo,
      storage: getLocalStorageStats(),
      consoleErrors: recentErrors()
    };
  },

  // A string avoids double serialization; drops whole entries rather than cutting JSON because the detail view parses it back.
  getDiagnosticSnapshotJson(): string {
    const snapshot = this.getDiagnosticSnapshot();
    let errors = snapshot.consoleErrors;

    for (;;) {
      const dropped = errors.length < snapshot.consoleErrors.length;
      const json = JSON.stringify(
        dropped
          ? { ...snapshot, consoleErrors: errors, truncated: true }
          : { ...snapshot, consoleErrors: errors }
      );

      if (new TextEncoder().encode(json).length <= MAX_SNAPSHOT_JSON_BYTES || errors.length === 0) {
        return json;
      }
      errors = errors.slice(1);
    }
  },

  getConsoleErrors(): BufferEntry[] {
    return [...errorBuffer];
  },

  clearConsoleErrors(): void {
    errorBuffer.length = 0;
  }
};

if (typeof window !== 'undefined') {
  (window as any).diagnosticsUtils = diagnosticsUtils;
}
